import React from "react";
import { ReactComponent as IconQuestion } from "@assets/Icons/Icon_Question.svg";
import { ReactComponent as IconSuccess } from "@assets/Icons/Icon_Success.svg";
import { ReactComponent as IconWarning } from "@assets/Icons/Icon_Warning.svg";

export interface GenericAlertProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;

    title?: string;
    /**
     * En lugar de `string`, lo hacemos `React.ReactNode`
     * para que acepte texto o elementos JSX.
     */
    description?: React.ReactNode;

    cancelLabel?: string;
    confirmLabel?: string;

    iconWidthClass?: string;
    variant?: "confirm" | "success" | "error" | "warning";

    children?: React.ReactNode;
    confirmButtonDisabled?: boolean;
    hideCancel?: boolean;
}

const GenericAlert: React.FC<GenericAlertProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title = "Are you sure you want to delete this item?",
    description = "More explanation",
    cancelLabel = "Cancel",
    confirmLabel = "Delete",
    iconWidthClass = "w-[5rem] h-10",
    variant = "confirm",
    children,
    confirmButtonDisabled = false, // Valor por defecto
    hideCancel = false,
}) => {
    if (!isOpen) return null;

    // Pick icon & colors
    let IconComp = IconQuestion;
    let containerBg = "bg-pink-50";
    let textColor = "text-pink-600";

    if (variant === "success") {
        IconComp = IconSuccess;
        containerBg = "bg-green-50";
        textColor = "text-green-600";
    } else if (variant === "error") {
        IconComp = IconWarning;
        containerBg = "bg-red-50";
        textColor = "text-red-600";
    } else if (variant === "warning") {
        IconComp = IconWarning;
        containerBg = "bg-yellow-50";
        textColor = "text-yellow-600";
    }

    const isSuccess = variant === "success";
    const showCancel = !isSuccess && !hideCancel;

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-6"
            aria-modal="true"
            role="dialog"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-30"
                onClick={onClose}
                role="button"
                aria-label="Close modal backdrop"
            />

            {/* Alert container */}
            <div className="relative z-10 w-full max-w-sm p-4 rounded-sm bg-white shadow-md">
                {/* Icon + Title + Description */}
                <div className="flex items-start gap-3 mb-3">
                    <div
                        className={`
              flex items-center justify-center
              rounded-md
              ${containerBg}
              ${textColor}
              ${iconWidthClass}
            `}
                    >
                        <IconComp className="w-5 h-5" />
                    </div>

                    <div>
                        <h2 className="text-base font-semibold text-black">{title}</h2>
                        {description && (
                            <p className="text-sm text-gray-600 mt-4">{description}</p>
                        )}

                        {/* Aquí se renderizan los children (por ej. el input "confirm") */}
                        {children && <div className="mt-3">{children}</div>}
                    </div>
                </div>

                <hr className="mb-3 border-gray-200" />

                {/* Footer with buttons */}
                <div className="flex items-center justify-end space-x-3">
                    {showCancel && (
                        <button type="button" onClick={onClose} className="text-sm font-medium text-gray-600 hover:text-gray-800">
                            {cancelLabel}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={confirmButtonDisabled}
                        className={`
              text-sm font-medium
              px-4 py-2
              rounded-md transition-colors
              ${variant === "confirm"
                                ? "bg-pink-100 text-pink-600 hover:bg-pink-200"
                                : variant === "success"
                                    ? "bg-green-100 text-green-600 hover:bg-green-200"
                                    : variant === "error"
                                        ? "bg-red-100 text-red-600 hover:bg-red-200"
                                        : "bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                            }
              ${confirmButtonDisabled ? "opacity-50 cursor-not-allowed" : ""}
            `}
                    >
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GenericAlert;
